import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable } from "@/components/admin/data-table";
import { ColumnDef } from "@tanstack/react-table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useUserCompanies } from "@/hooks/useUserCompanies";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  useMaintenancePlans, 
  useCreateMaintenancePlan, 
  useUpdateMaintenancePlan, 
  useDeleteMaintenancePlan,
  MaintenancePlan,
  calculatePlanStatus
} from "@/hooks/use-maintenance-plans";
import { 
  useWorkOrders, 
  useCreateWorkOrder, 
  useUpdateWorkOrder, 
  useCloseWorkOrder,
  useDeleteWorkOrder,
  WorkOrder,
  formatWorkOrderNumber
} from "@/hooks/use-work-orders";
import { useFleetProviders } from "@/hooks/use-fleet-providers";
import { MaintenancePlanFormSheet } from "@/components/fleet/MaintenancePlanFormSheet";
import { WorkOrderFormSheet } from "@/components/fleet/WorkOrderFormSheet";
import { OpenWorkOrdersDialog } from "@/components/fleet/OpenWorkOrdersDialog";
import { useFleetPermissions } from "@/hooks/use-fleet-permissions";
import { InlineStatusSelect } from "@/components/fleet/InlineStatusSelect";
import { InlineCostInput } from "@/components/fleet/InlineCostInput";
import { GroupedMaintenancePlansTable } from "@/components/fleet/GroupedMaintenancePlansTable";
import { 
  Plus, 
  Edit, 
  Eye, 
  Trash2, 
  Wrench, 
  Calendar, 
  CheckCircle, 
  AlertTriangle,
  AlertCircle,
  MoreHorizontal 
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Translation helper functions
const getMaintenanceTypeLabel = (type: string): string => {
  const typeLabels: Record<string, string> = {
    preventive: "Preventiva",
    corrective: "Corretiva",
    oil_change: "Troca de Óleo",
    tire_rotation: "Rodízio de Pneus",
    inspection: "Inspeção",
    other: "Outros"
  };
  return typeLabels[type] || type;
};

const getMaintenanceStatusLabel = (status: string): string => {
  const statusLabels: Record<string, string> = {
    pending: "Pendente",
    active: "Ativo",
    inactive: "Inativo",
    completed: "Concluído",
    in_progress: "Em Andamento"
  };
  return statusLabels[status] || status;
};

const getWorkOrderStatusLabel = (status: string): string => {
  const statusLabels: Record<string, string> = {
    open: "Aberto",
    in_progress: "Em Andamento",
    closed: "Fechado",
    cancelled: "Cancelado"
  };
  return statusLabels[status] || status;
};

export default function FleetMaintenance() {
  const { profile } = useAuth();
  const { canCreate, canEdit, canDelete, isViewer } = useFleetPermissions();
  const [selectedPlan, setSelectedPlan] = useState<MaintenancePlan | null>(null);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState<WorkOrder | null>(null);
  const [isCreatePlanOpen, setIsCreatePlanOpen] = useState(false);
  const [isCreateWorkOrderOpen, setIsCreateWorkOrderOpen] = useState(false);
  const [isEditPlanOpen, setIsEditPlanOpen] = useState(false);
  const [isEditWorkOrderOpen, setIsEditWorkOrderOpen] = useState(false);
  const [selectedPlanForOS, setSelectedPlanForOS] = useState<MaintenancePlan | null>(null);
  const [viewingPlanId, setViewingPlanId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"pending" | "active" | "inactive" | "completed" | "in_progress">("active");

  // Get current user's company with dedicated hook
  const { data: userCompanies, isLoading: isLoadingCompanies, error: companiesError } = useUserCompanies();
  
  const currentCompanyId = userCompanies?.[0]?.company_id;
  
  console.log('🏢 Maintenance Page - Current Company ID:', currentCompanyId, 'Companies:', userCompanies);

  // Fetch data using the new hooks
  const { data: maintenancePlans = [], isLoading: isLoadingPlans } = useMaintenancePlans(currentCompanyId);
  const { data: workOrders = [], isLoading: isLoadingOrders, error: ordersError } = useWorkOrders(currentCompanyId);
  const { data: providers = [] } = useFleetProviders(currentCompanyId);
  
  // Calculate dynamic status based on work orders
  const plansWithDynamicStatus = maintenancePlans.map(plan => ({
    ...plan,
    displayStatus: calculatePlanStatus(plan.status, plan.id, workOrders)
  }));
  
  // Filter plans by calculated status for tabs
  const pendingPlansData = plansWithDynamicStatus.filter(plan => plan.displayStatus === "pending");
  const activePlansData = plansWithDynamicStatus.filter(plan => plan.displayStatus === "active" || plan.displayStatus === "in_progress");
  const inactivePlansData = plansWithDynamicStatus.filter(plan => plan.displayStatus === "inactive");
  const completedPlansData = plansWithDynamicStatus.filter(plan => plan.displayStatus === "completed");
  const inProgressPlansData = plansWithDynamicStatus.filter(plan => plan.displayStatus === "in_progress");
  
  // Fetch vehicles for dropdowns
  const { data: vehicles = [] } = useQuery({
    queryKey: ["fleetVehicles", currentCompanyId],
    queryFn: async () => {
      if (!currentCompanyId) return [];
      const { data } = await supabase
        .from("fleet_vehicles")
        .select("id, plate, make, model")
        .eq("company_id", currentCompanyId)
        .eq("status", "active");
      return data || [];
    },
    enabled: !!currentCompanyId
  });

  // Mutations
  const createPlanMutation = useCreateMaintenancePlan(currentCompanyId || "");
  const updatePlanMutation = useUpdateMaintenancePlan(currentCompanyId || "");
  const deletePlanMutation = useDeleteMaintenancePlan(currentCompanyId || "");
  
  const createWorkOrderMutation = useCreateWorkOrder(currentCompanyId || "");
  const updateWorkOrderMutation = useUpdateWorkOrder(currentCompanyId || "");
  const closeWorkOrderMutation = useCloseWorkOrder(currentCompanyId || "");
  const deleteWorkOrderMutation = useDeleteWorkOrder(currentCompanyId || "");

  // Handlers
  const handleCreatePlan = async (data: any) => {
    const { __createWorkOrder, __workOrderData, ...planFields } = data;
    
    try {
      const newPlan = await createPlanMutation.mutateAsync(planFields);
      
      // Se o usuário marcou para criar OS junto, criar a OS vinculada
      if (__createWorkOrder && __workOrderData && newPlan?.id) {
        // Validar provider_id para evitar strings vazias
        const providerId = __workOrderData.provider_id && 
                          __workOrderData.provider_id !== 'none' && 
                          __workOrderData.provider_id.trim() !== '' 
                          ? __workOrderData.provider_id 
                          : null;
        
        await createWorkOrderMutation.mutateAsync({
          vehicle_id: newPlan.vehicle_id,
          plan_id: newPlan.id,
          provider_id: providerId,
          description: __workOrderData.description,
          status: 'open',
          total_cost: __workOrderData.total_cost ? parseFloat(__workOrderData.total_cost) : null,
          opened_at: new Date(__workOrderData.opened_at).toISOString(),
          order_number: 0
        });
      }
      
      setIsCreatePlanOpen(false);
    } catch (error) {
      console.error('Error in handleCreatePlan:', error);
    }
  };

  const handleUpdatePlan = async (data: any) => {
    const { __createWorkOrder, __workOrderData, id, ...planFields } = data;
    
    // Validar que id existe
    if (!id) {
      console.error('❌ ID do plano não foi fornecido para atualização');
      return;
    }
    
    try {
      const updatedPlan = await updatePlanMutation.mutateAsync({ id, ...planFields });
      
      // Se o usuário marcou para criar OS junto e o plano está ativo, criar a OS
      if (__createWorkOrder && __workOrderData && updatedPlan?.id && updatedPlan.status === 'active') {
        // Validar provider_id para evitar strings vazias
        const providerId = __workOrderData.provider_id && 
                          __workOrderData.provider_id !== 'none' && 
                          __workOrderData.provider_id.trim() !== '' 
                          ? __workOrderData.provider_id 
                          : null;
        
        await createWorkOrderMutation.mutateAsync({
          vehicle_id: updatedPlan.vehicle_id,
          plan_id: updatedPlan.id,
          provider_id: providerId,
          description: __workOrderData.description,
          status: 'open',
          total_cost: __workOrderData.total_cost ? parseFloat(__workOrderData.total_cost) : null,
          opened_at: new Date(__workOrderData.opened_at).toISOString(),
          order_number: 0
        });
      }
      
      setIsEditPlanOpen(false);
      setSelectedPlan(null);
    } catch (error) {
      console.error('Error in handleUpdatePlan:', error);
    }
  };

  const handleCreateWorkOrder = (data: any) => {
    createWorkOrderMutation.mutate(data, {
      onSuccess: () => setIsCreateWorkOrderOpen(false)
    });
  };

  const handleUpdateWorkOrder = (data: any) => {
    updateWorkOrderMutation.mutate(data, {
      onSuccess: () => {
        setIsEditWorkOrderOpen(false);
        setSelectedWorkOrder(null);
      }
    });
  };

  const handleUpdateWorkOrderStatus = (id: string, status: string) => {
    updateWorkOrderMutation.mutate({ id, status });
  };

  const handleUpdateWorkOrderCost = (id: string, total_cost: number | null) => {
    updateWorkOrderMutation.mutate({ id, total_cost });
  };

  const handleEditPlan = (plan: MaintenancePlan) => {
    setSelectedPlan(plan);
    setIsEditPlanOpen(true);
  };

  const handleEditWorkOrder = (workOrder: WorkOrder) => {
    setSelectedWorkOrder(workOrder);
    setIsEditWorkOrderOpen(true);
  };

  const handleCreateWorkOrderFromPlan = (plan: MaintenancePlan) => {
    setSelectedPlanForOS(plan);
    setIsCreateWorkOrderOpen(true);
  };

  const handleViewOpenOrders = (planId: string) => {
    setViewingPlanId(planId);
  };

  const handleCloseWorkOrderFromDialog = (id: string) => {
    closeWorkOrderMutation.mutate({ id });
  };

  const maintenanceColumns: ColumnDef<MaintenancePlan>[] = [
    {
      accessorKey: "name",
      header: "Nome do Plano",
      cell: ({ row }) => {
        const name = row.getValue("name") as string;
        return <span>{name.toUpperCase()}</span>;
      },
    },
    {
      accessorKey: "type",
      header: "Tipo",
      cell: ({ row }) => {
        const type = row.getValue("type") as string;
        return <Badge variant="outline">{getMaintenanceTypeLabel(type)}</Badge>;
      },
    },
    {
      accessorKey: "fleet_vehicles.plate",
      header: "Veículo",
      cell: ({ row }) => {
        const vehicle = row.original.fleet_vehicles;
        return vehicle ? `${vehicle.plate} - ${vehicle.make} ${vehicle.model}` : "N/A";
      },
    },
    {
      accessorKey: "periodicity_days",
      header: "Periodicidade",
      cell: ({ row }) => {
        const days = row.getValue("periodicity_days") as number;
        const km = row.original.periodicity_km;
        return days ? `${days} dias` : km ? `${km} km` : "N/A";
      },
    },
    {
      accessorKey: "next_execution_date",
      header: "Próxima Execução",
      cell: ({ row }) => {
        const plan = row.original;
        
        // Se a periodicidade é em KM e há próxima execução em KM, mostrar KM
        if (plan.periodicity_km && plan.next_execution_km) {
          return (
            <Badge variant="secondary" className="font-mono">
              {plan.next_execution_km.toLocaleString('pt-BR')} km
            </Badge>
          );
        }
        
        // Senão, mostrar a data (se existir)
        const date = row.getValue("next_execution_date") as string;
        if (date) {
          return (
            <Badge variant="outline" className="font-mono">
              {new Date(date).toLocaleDateString("pt-BR")}
            </Badge>
          );
        }
        
        return <span className="text-muted-foreground">N/A</span>;
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.getValue("status") as string;
        const approvalStatus = row.original.approval_status;
        const variant = status === "active" ? "default" : "secondary";
        
        return (
          <div className="flex gap-2 items-center flex-wrap">
            <Badge variant={variant}>{getMaintenanceStatusLabel(status)}</Badge>
            
            {approvalStatus === 'pending' && (
              <Badge variant="warning" className="bg-yellow-500 hover:bg-yellow-600 text-white">
                ⏳ Aguardando
              </Badge>
            )}
            
            {approvalStatus === 'rejected' && (
              <Badge variant="destructive">
                ❌ Rejeitado
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      id: "work_orders_count",
      header: "OSs",
      cell: ({ row }) => {
        const plan = row.original;
        const planWorkOrders = workOrders.filter(wo => wo.plan_id === plan.id);
        const openOrders = planWorkOrders.filter(wo => wo.status === 'open' || wo.status === 'in_progress');
        
        return (
          <div className="flex gap-2 items-center">
            <Badge variant="secondary">
              {planWorkOrders.length}
            </Badge>
            {openOrders.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleViewOpenOrders(plan.id)}
                className="h-6 px-2"
              >
                <Badge variant="destructive">
                  {openOrders.length} {openOrders.length === 1 ? 'Aberta' : 'Abertas'}
                </Badge>
              </Button>
            )}
          </div>
        );
      }
    },
    {
      id: "actions",
      header: "Ações",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleEditPlan(row.original)} disabled={!canEdit}>
              <Edit className="mr-2 h-4 w-4" />
              Editar Plano
            </DropdownMenuItem>
            
            {row.original.status === 'active' && canCreate && (
              <DropdownMenuItem 
                onClick={() => handleCreateWorkOrderFromPlan(row.original)}
              >
                <Plus className="mr-2 h-4 w-4" />
                Criar Nova OS
              </DropdownMenuItem>
            )}
            
            {(row.original.status === 'inactive' || row.original.status === 'completed') && canEdit && (
              <DropdownMenuItem 
                onClick={() => {
                  updatePlanMutation.mutate({ 
                    id: row.original.id, 
                    status: 'active' 
                  });
                }}
              >
                <CheckCircle className="mr-2 h-4 w-4 text-green-600" />
                Reativar Plano
              </DropdownMenuItem>
            )}
            
            {canDelete && (
              <DropdownMenuItem 
                onClick={() => deletePlanMutation.mutate(row.original.id)}
                className="text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Excluir
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const workOrderColumns: ColumnDef<WorkOrder>[] = [
    {
      accessorKey: "order_number",
      header: "N° OS",
      cell: ({ row }) => {
        return (
          <Badge variant="secondary" className="font-mono">
            {formatWorkOrderNumber(row.original.order_number)}
          </Badge>
        );
      },
    },
    {
      accessorKey: "fleet_vehicles.plate",
      header: "Veículo",
      cell: ({ row }) => {
        const vehicle = row.original.fleet_vehicles;
        return vehicle ? `${vehicle.plate} - ${vehicle.make} ${vehicle.model}` : "N/A";
      },
    },
    {
      accessorKey: "description",
      header: "Descrição",
      cell: ({ row }) => {
        const description = row.getValue("description") as string;
        if (!description) return "N/A";
        
        const upperDescription = description.toUpperCase();
        return upperDescription.length > 50 
          ? `${upperDescription.substring(0, 50)}...` 
          : upperDescription;
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <InlineStatusSelect
          workOrder={row.original}
          onUpdate={handleUpdateWorkOrderStatus}
          disabled={!canEdit}
        />
      ),
    },
    {
      accessorKey: "total_cost",
      header: "Custo Total",
      cell: ({ row }) => (
        <InlineCostInput
          workOrderId={row.original.id}
          currentCost={row.getValue("total_cost")}
          onUpdate={handleUpdateWorkOrderCost}
          disabled={!canEdit}
        />
      ),
    },
    {
      accessorKey: "opened_at",
      header: "Data de Abertura",
      cell: ({ row }) => {
        const date = row.getValue("opened_at") as string;
        return new Date(date).toLocaleDateString("pt-BR");
      },
    },
    {
      id: "actions",
      header: "Ações",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleEditWorkOrder(row.original)} disabled={!canEdit}>
              <Edit className="mr-2 h-4 w-4" />
              Editar
            </DropdownMenuItem>
            {canEdit && row.original.status === 'open' && (
              <DropdownMenuItem 
                onClick={() => closeWorkOrderMutation.mutate({ id: row.original.id })}
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                Fechar
              </DropdownMenuItem>
            )}
            {canDelete && (
              <DropdownMenuItem 
                onClick={() => deleteWorkOrderMutation.mutate(row.original.id)}
                className="text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Excluir
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  // Summary cards data
  const totalPlans = maintenancePlans.length;
  const pendingPlans = pendingPlansData.length;
  const activePlans = activePlansData.length;
  const inactivePlans = inactivePlansData.length;
  const completedPlans = completedPlansData.length;
  const inProgressPlans = inProgressPlansData.length;
  const totalWorkOrders = workOrders.length;
  const openWorkOrders = workOrders.filter(order => order.status === "open").length;

  // Loading state
  if (isLoadingCompanies) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Manutenção</h1>
            <p className="text-muted-foreground">Carregando...</p>
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader className="space-y-0 pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Error state for companies
  if (companiesError) {
    return (
      <div className="space-y-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Erro ao carregar empresas</AlertTitle>
          <AlertDescription>
            Ocorreu um erro ao buscar suas empresas. Por favor, tente novamente.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // No company state
  if (!currentCompanyId) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Manutenção</h1>
            <p className="text-muted-foreground">
              Gerencie planos de manutenção e ordens de serviço da frota
            </p>
          </div>
        </div>
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Nenhuma empresa encontrada</AlertTitle>
          <AlertDescription>
            Você não está associado a nenhuma empresa. Entre em contato com o administrador do sistema.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Manutenção</h1>
          <p className="text-muted-foreground">
            Gerencie planos de manutenção e ordens de serviço da frota
          </p>
        </div>
        <div className="flex gap-2 items-center">
          {isViewer && (
            <Badge variant="secondary">
              👁️ Modo Visualização
            </Badge>
          )}
          <Button onClick={() => setIsCreatePlanOpen(true)} disabled={!canCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Novo Plano
          </Button>
          <Button onClick={() => setIsCreateWorkOrderOpen(true)} disabled={!canCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Nova OS
          </Button>
        </div>
      </div>

      {/* Error state for work orders */}
      {ordersError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Erro ao carregar ordens de serviço</AlertTitle>
          <AlertDescription>
            Ocorreu um erro ao buscar as ordens de serviço. Por favor, tente novamente.
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-7">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Planos</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalPlans}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Planos Pendentes</CardTitle>
            <AlertCircle className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{pendingPlans}</div>
            <p className="text-xs text-muted-foreground">
              Aprovados, aguardando execução
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Planos Ativos</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activePlans}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Em Andamento</CardTitle>
            <Wrench className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{inProgressPlans}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Planos Inativos</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{inactivePlans}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Planos Concluídos</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{completedPlans}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ordens de Serviço</CardTitle>
            <Wrench className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalWorkOrders}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ordens Abertas</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{openWorkOrders}</div>
          </CardContent>
        </Card>
      </div>

      {/* Maintenance Plans with Tabs */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Planos de Manutenção</CardTitle>
              <CardDescription>
                Gerencie os planos de manutenção preventiva dos veículos
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)} className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="pending" className="relative">
                Pendentes
                {pendingPlans > 0 && (
                  <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                    {pendingPlans}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="active" className="relative">
                Ativos
                {(activePlans + inProgressPlans) > 0 && (
                  <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                    {activePlans + inProgressPlans}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="inactive" className="relative">
                Inativos
                {inactivePlans > 0 && (
                  <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                    {inactivePlans}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="completed" className="relative">
                Concluídos
                {completedPlans > 0 && (
                  <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                    {completedPlans}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="pending" className="mt-4">
              {pendingPlansData.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold">Nenhum plano pendente</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Planos aprovados aguardando primeira Ordem de Serviço ou ativação manual aparecerão aqui.
                  </p>
                </div>
              ) : (
                <GroupedMaintenancePlansTable
                  plans={pendingPlansData}
                  workOrders={workOrders}
                  onEditPlan={handleEditPlan}
                  onDeletePlan={(planId) => deletePlanMutation.mutate(planId)}
                  onCreateWorkOrder={handleCreateWorkOrderFromPlan}
                  onReactivatePlan={(planId) => updatePlanMutation.mutate({ id: planId, status: 'active' })}
                  onViewOpenOrders={handleViewOpenOrders}
                  canEdit={canEdit}
                  canCreate={canCreate}
                  canDelete={canDelete}
                />
              )}
            </TabsContent>

            <TabsContent value="active" className="mt-4">
              {activePlansData.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold">Nenhum plano ativo</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Crie um novo plano de manutenção para começar.
                  </p>
                </div>
              ) : (
                <GroupedMaintenancePlansTable
                  plans={activePlansData}
                  workOrders={workOrders}
                  onEditPlan={handleEditPlan}
                  onDeletePlan={(planId) => deletePlanMutation.mutate(planId)}
                  onCreateWorkOrder={handleCreateWorkOrderFromPlan}
                  onReactivatePlan={(planId) => updatePlanMutation.mutate({ id: planId, status: 'active' })}
                  onViewOpenOrders={handleViewOpenOrders}
                  canEdit={canEdit}
                  canCreate={canCreate}
                  canDelete={canDelete}
                />
              )}
            </TabsContent>


            <TabsContent value="inactive" className="mt-4">
              {inactivePlansData.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold">Nenhum plano inativo</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Planos inativos aparecerão aqui.
                  </p>
                </div>
              ) : (
                <GroupedMaintenancePlansTable
                  plans={inactivePlansData}
                  workOrders={workOrders}
                  onEditPlan={handleEditPlan}
                  onDeletePlan={(planId) => deletePlanMutation.mutate(planId)}
                  onCreateWorkOrder={handleCreateWorkOrderFromPlan}
                  onReactivatePlan={(planId) => updatePlanMutation.mutate({ id: planId, status: 'active' })}
                  onViewOpenOrders={handleViewOpenOrders}
                  canEdit={canEdit}
                  canCreate={canCreate}
                  canDelete={canDelete}
                />
              )}
            </TabsContent>

            <TabsContent value="completed" className="mt-4">
              {completedPlansData.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center">
                  <CheckCircle className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold">Nenhum plano concluído</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    Planos concluídos aparecerão aqui.
                  </p>
                </div>
              ) : (
                <GroupedMaintenancePlansTable
                  plans={completedPlansData}
                  workOrders={workOrders}
                  onEditPlan={handleEditPlan}
                  onDeletePlan={(planId) => deletePlanMutation.mutate(planId)}
                  onCreateWorkOrder={handleCreateWorkOrderFromPlan}
                  onReactivatePlan={(planId) => updatePlanMutation.mutate({ id: planId, status: 'active' })}
                  onViewOpenOrders={handleViewOpenOrders}
                  canEdit={canEdit}
                  canCreate={canCreate}
                  canDelete={canDelete}
                />
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Work Orders */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Ordens de Serviço</CardTitle>
              <CardDescription>
                Acompanhe as ordens de serviço e manutenções realizadas
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoadingOrders ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            <DataTable columns={workOrderColumns} data={workOrders as any} />
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <MaintenancePlanFormSheet
        isOpen={isCreatePlanOpen}
        onClose={() => setIsCreatePlanOpen(false)}
        onSubmit={handleCreatePlan}
        vehicles={vehicles}
        providers={providers as any}
        isLoading={createPlanMutation.isPending}
        onCreateWorkOrder={() => {}}
        workOrders={workOrders}
      />

      <MaintenancePlanFormSheet
        isOpen={isEditPlanOpen}
        onClose={() => {
          setIsEditPlanOpen(false);
          setSelectedPlan(null);
        }}
        onSubmit={handleUpdatePlan}
        plan={selectedPlan}
        vehicles={vehicles}
        providers={providers as any}
        isLoading={updatePlanMutation.isPending}
        onCreateWorkOrder={() => {}} // Dummy function para ativar a seção de criação de OS
        workOrders={workOrders}
      />

      <WorkOrderFormSheet
        open={isCreateWorkOrderOpen}
        onOpenChange={(open) => {
          setIsCreateWorkOrderOpen(open);
          if (!open) setSelectedPlanForOS(null);
        }}
        onSubmit={handleCreateWorkOrder}
        vehicles={vehicles}
        maintenancePlans={maintenancePlans}
        providers={providers as any}
        isLoading={createWorkOrderMutation.isPending}
        preSelectedPlanId={selectedPlanForOS?.id}
        preSelectedVehicleId={selectedPlanForOS?.vehicle_id}
      />

      <WorkOrderFormSheet
        open={isEditWorkOrderOpen}
        onOpenChange={(open) => {
          setIsEditWorkOrderOpen(open);
          if (!open) setSelectedWorkOrder(null);
        }}
        onSubmit={handleUpdateWorkOrder}
        workOrder={selectedWorkOrder}
        vehicles={vehicles}
        maintenancePlans={maintenancePlans}
        providers={providers as any}
        isLoading={updateWorkOrderMutation.isPending}
      />

      <OpenWorkOrdersDialog
        isOpen={!!viewingPlanId}
        onClose={() => setViewingPlanId(null)}
        plan={maintenancePlans.find(p => p.id === viewingPlanId) || null}
        workOrders={workOrders}
        onEditWorkOrder={handleEditWorkOrder}
        onCloseWorkOrder={handleCloseWorkOrderFromDialog}
      />
    </div>
  );
}